/*
 * The RBAC editor as a page inside Clerk's OrganizationProfile modal.
 *
 * WHY THERE, AND NOT A PAGE IN AN APP. Permissions are a property of the ORG, so
 * they belong where an admin already manages the org — beside General and
 * Members, in the modal every UI already mounts behind the org switcher. A page
 * in the fleet admin would have been a page the other three apps never show, for
 * a question all four of them ask. <vitesse-billing-panel> lives in this same
 * modal for exactly the same reason.
 *
 * ONE FACTORY, FOUR CALLERS. The Clerk custom-page object is fiddly (mount /
 * unmount / mountIcon / unmountIcon, and a leaked element on re-mount is a
 * duplicated panel), so it is written once here. Each app supplies only what is
 * genuinely its own: the roster it read from Clerk, and the save that goes
 * through ITS transport.
 *
 * base-ui holds no transport by design, which is why `save` is a callback rather
 * than an RPC. An app that cannot write — or a viewer who may not — passes none,
 * and the panel renders read-only rather than offering a button that fails.
 */
import { toMember } from './org-members';
import { t } from './i18n';
import { membersPage } from './members-page';
import { auditPage } from './org-audit-page';
import { billingPage, warmBilling } from './billing-page';
import './elements/vitesse-rbac-panel';
/* A key, matching the stroke-width and 24-box of the icons Clerk's own nav uses,
 * so the row does not look pasted in beside General and Members. */
const KEY_ICON = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" ' +
    'stroke-linecap="round" stroke-linejoin="round"><circle cx="7.5" cy="15.5" r="4.5"/>' +
    '<path d="m21 2-9.6 9.6"/><path d="m15.5 7.5 3 3L22 7l-3-3"/></svg>';
let warm;
/** Start the editor's reads for `orgId` if they are not already in flight. */
export function warmRbac(orgId, load) {
    if (!orgId)
        return;
    if (warm?.orgId !== orgId)
        warm = { orgId };
    warm.members ??= load.members ? keep(orgId, 'members', () => Promise.resolve(load.members())) : undefined;
    warm.roles ??= load.roles ? keep(orgId, 'roles', load.roles) : undefined;
    warm.audit ??= load.audit ? keep(orgId, 'audit', load.audit) : undefined;
}
/*
 * A warmed read that does NOT cache its own failure.
 *
 * This previously ended `.catch(() => [])`, which turned a failed speculative
 * fetch into a cached SUCCESSFUL EMPTY RESULT — and the warm runs when the org
 * switcher mounts, which is early enough that the session token may not be ready.
 * One 401 at boot and the org's roles were an empty array for the lifetime of the
 * tab: every later mount reused the poisoned entry, the RBAC page showed no
 * roles, and reloading did not help because the next boot warmed the same way.
 * The RPC itself was fine the whole time.
 *
 * So a rejection CLEARS the slot instead of resolving it. The next reader misses
 * the cache and asks again — which is what a speculative cache should do with a
 * result it never actually got. The extra `.catch` keeps the rejection handled so
 * a failed warm nobody is waiting on stays out of the console.
 */
function keep(orgId, key, run) {
    const p = run();
    p.catch(() => {
        if (warm?.orgId === orgId)
            warm[key] = undefined;
    });
    return p;
}
/**
 * The warmed value for `orgId`, or undefined on a miss.
 *
 * Exported so the org-keying can be tested as the invariant it is, rather than
 * only through a mount — the DOM-free test path is where the rule that a
 * different org MISSES belongs, since that rule is not about rendering.
 */
export function warmed(orgId, key) {
    return warm?.orgId === orgId ? warm[key] : undefined;
}
/**
 * Drop one warmed entry, because it is now known to be behind the server.
 *
 * THE WARM CACHE OUTLIVES THE PANEL, and that is the whole bug it caused. The
 * panel re-mounts every time somebody switches tabs in the org dialog, and it
 * re-reads the warm entry when it does — so a role saved a moment ago, applied
 * correctly to the panel's own state and genuinely persisted on the server, was
 * overwritten by the stale list on the next tab switch. It came back after a full
 * reload, which is the shape that makes it read as "the save did not work".
 *
 * Proven against production rather than reasoned about: a role created through the
 * real UI was absent from the list, present after a reload, and its
 * rbac.org_roles_changed audit event had already landed 203ms after the click. The
 * write was never in doubt; the cache was.
 *
 * Invalidating rather than rewriting is deliberate. The server is the authority on
 * what the role set now is — a save can normalise names, drop an empty grant, or
 * be raced by another admin — so the next read should ASK rather than trust what
 * this browser thought it sent.
 */
export function invalidateWarm(key) {
    if (warm)
        warm[key] = undefined;
}
export function rbacPage(opts) {
    let panel;
    return {
        label: t('perm.title'),
        url: 'rbac',
        mountIcon: (el) => {
            el.innerHTML = KEY_ICON;
        },
        unmountIcon: () => { },
        mount: (el) => {
            // Same remount hazard as the billing page: Clerk can call mount on a container still
            // holding the previous mount's children. Replace them whole.
            el.replaceChildren();
            panel = document.createElement('vitesse-rbac-panel');
            // The gate overlay's Upgrade bubbles, but the modal is portaled outside the app-shell, so it
            // is handled HERE (where the panel lives) rather than left to reach a listener it cannot.
            panel.addEventListener('vitesse-gate-upgrade', (e) => {
                e.stopPropagation();
                opts.onUpgrade?.();
            });
            panel.canEdit = opts.canEdit?.() ?? Boolean(opts.save);
            panel.canEditRoles = opts.canEditRoles?.() ?? Boolean(opts.saveRoles);
            if (opts.save)
                panel.save = opts.save;
            if (opts.saveRoles) {
                /*
                 * The save is WRAPPED so the warm entry cannot outlive the truth.
                 *
                 * This panel is re-created on every tab switch in the org dialog and
                 * re-reads the warm roles on mount, so without this a role saved a moment
                 * ago is replaced by the pre-save list the next time somebody clicks
                 * People and back — while the server, correctly, has the new one. It
                 * looks exactly like a save that silently failed.
                 *
                 * Invalidated only AFTER the call resolves: a failed save leaves the warm
                 * entry alone, because a read that has not changed is still the best
                 * answer available and dropping it would trade a wrong list for no list.
                 */
                const saveRoles = opts.saveRoles;
                panel.saveRoles = async (roles) => {
                    await saveRoles(roles);
                    invalidateWarm('roles');
                };
            }
            if (opts.setOrgRole)
                panel.setOrgRole = opts.setOrgRole;
            // Roles load ASYNCHRONOUSLY and the panel renders without them: a slow or
            // failing ListOrgRoles must not blank the members list, which is the half
            // of this screen that needs no RPC at all.
            const created = panel;
            // Read at MOUNT, not at factory time: the modal is opened long after boot,
            // and an org switch between the two would otherwise show the previous org's
            // roster — the one screen where showing the wrong org's people is worst.
            // Awaited because it is an RPC now; the panel renders its empty state until
            // it lands rather than blocking the tab from opening.
            const org = opts.orgId?.() ?? '';
            // The warm entry holds the AUTHORED half; the join with the live roster
            // happens now, so a roster that arrived after warming is still used.
            void Promise.resolve(opts.members(warmed(org, 'members')))
                .then((m) => {
                created.members = m;
            })
                .catch((e) => console.warn('member roster unavailable', e));
            // A warmed read that FAILED falls back to a live one rather than leaving the
            // page empty: the warm is an optimisation, and an optimisation that can turn
            // into "you have no roles" is not one.
            const rolesRead = () => {
                const w = warmed(org, 'roles');
                if (!w)
                    return opts.roles?.();
                return w.catch(() => opts.roles?.() ?? Promise.resolve([]));
            };
            void rolesRead()
                ?.then((r) => {
                created.roles = r;
            })
                .catch((e) => console.warn('org roles unavailable', e));
            // Not warmed and not awaited: the whole permission editor renders without
            // it, and its absence costs one row's worth of granularity rather than the
            // screen. A failure here must never be the reason roles cannot be edited.
            void opts
                .specialists?.()
                .then((sp) => {
                created.specialists = sp;
            })
                .catch((e) => console.warn('specialists unavailable', e));
            el.appendChild(panel);
        },
        unmount: () => {
            panel?.remove();
            panel = undefined;
        },
    };
}
/** The roster, plus the write that changes a member's CLERK org role.
 *
 *  Returned together because the write needs the membership OBJECT the read
 *  produced: Clerk exposes `update()` on the membership, not a by-id call on the
 *  organization, so a roster that discarded them would leave the org role
 *  readable and unchangeable — which is how it came to look bolted-on. */
export function orgRoster(clerk) {
    let roster = [];
    let handles = new Map();
    const refresh = () => {
        const org = clerk.organization;
        const get = org?.getMemberships;
        if (!org || !get)
            return;
        void (async () => {
            const page = (await get.call(org, { pageSize: 100 }));
            const list = Array.isArray(page) ? page : (page?.data ?? []);
            const next = new Map();
            roster = list
                .map((raw) => {
                const m = raw;
                const mapped = toMember(m);
                if (mapped.userId)
                    next.set(mapped.userId, m);
                return mapped;
            })
                .filter((m) => m.userId);
            handles = next;
        })().catch((e) => console.warn('member roster unavailable', e));
    };
    refresh();
    clerk.addListener?.(refresh); // an org switch must not leave the previous org's people on screen
    return {
        members: () => roster,
        setOrgRole: async (userId, role) => {
            const membership = handles.get(userId);
            // Clerk's wire form is prefixed; the editor speaks the bare word, the same
            // one normalizeRole() produces on the way in.
            if (!membership?.update)
                throw new Error('this surface cannot change org roles');
            await membership.update({ role: `org:${role}` });
            refresh();
        },
        /*
         * Remove a person from the org. Same shape as the role write and for the same
         * reason: the provider hangs it off the MEMBERSHIP, so only a roster that kept
         * the handles can offer it at all.
         *
         * No confirmation here — the panel owns that question. This is the write.
         */
        removeMember: async (userId) => {
            const membership = handles.get(userId);
            if (!membership?.destroy)
                throw new Error('this surface cannot remove members');
            await membership.destroy();
            refresh();
        },
    };
}
/*
 * PRESENCE from the identity provider, AUTHORED STATE from our fabric, joined.
 *
 * They are two halves of one row and neither can stand in for the other. The
 * provider knows WHO IS IN the org and nothing about what an administrator has
 * granted them; the fabric knows what was granted and deliberately returns only
 * the people it has an opinion about. Preferring the fabric list — which is what
 * this did — renders an EMPTY editor for a real org until somebody has already
 * authored something, which is exactly when an admin first opens it.
 *
 * The provider's roster is the LEFT side, so a member with nothing authored still
 * appears, with an empty matrix meaning "the role decides". An authored record
 * belonging to someone who has since left has no row to attach to and does not
 * resurrect them.
 */
export function joinRoster(presence, authored) {
    if (!authored)
        return presence;
    return async (warm) => {
        // Presence read HERE, not when this closure was built: the provider fills its
        // roster asynchronously, and reading it early is what made the editor show an
        // empty org.
        const people = presence();
        let byUser = new Map();
        try {
            byUser = new Map((await (warm ?? authored())).map((m) => [m.userId, m]));
        }
        catch {
            // A failed authored read must not empty the roster: the people are real
            // either way, and showing none of them is a worse answer than showing them
            // with role defaults. The editor's own save path surfaces the failure.
        }
        return people.map((p) => {
            const a = byUser.get(p.userId);
            return a ? { ...p, grants: a.grants, assignedRole: a.assignedRole, overrides: a.overrides } : p;
        });
    };
}
/** The active org's id, or '' when there is none. Structural read — base-ui
 *  imports no SDK. */
function currentOrgId(clerk) {
    return clerk.organization?.id ?? '';
}
/**
 * Open the organization profile focused on its Billing page — for any surface that needs to send a
 * user to upgrade (a gated view's "Upgrade" action) without knowing how the dialog is mounted.
 *
 * Best-effort deep-link: Clerk's modal has no public "open on this custom page" argument, so this
 * opens the profile and then activates the Billing nav item — the button a user would click — as
 * soon as the dialog's navbar renders, matched by the Billing page's own localised label so it
 * works in every language. If that item never appears the dialog just stays on its default page,
 * which is an acceptable fallback.
 */
export function openOrgBilling(clerk) {
    openOrgPage(clerk, t('billing.title'));
}
/**
 * The same deep-link, aimed at the permissions page.
 *
 * Exists so a screen that needs a role can send someone to author one without
 * losing what they were doing: the org dialog is PORTALED, so it opens over
 * whatever is already on screen and closing it reveals that again, unchanged.
 * The alternative — navigating the app — would discard an unsaved specialist to
 * go and create the role that specialist was waiting for.
 */
export function openOrgRbac(clerk) {
    openOrgPage(clerk, t('perm.title'));
}
function openOrgPage(clerk, wanted) {
    const c = clerk;
    if (typeof c.openOrganizationProfile !== 'function')
        return;
    c.openOrganizationProfile();
    const deadline = Date.now() + 4000;
    const select = () => {
        const nav = Array.from(document.querySelectorAll('.cl-navbarButton')).find((e) => e.textContent?.trim() === wanted);
        if (nav) {
            nav.click();
            return;
        }
        if (Date.now() < deadline)
            requestAnimationFrame(select);
    };
    requestAnimationFrame(select);
}
export function mountOrgSwitcher(clerk, el, opts = {}) {
    // The roster is per-CLERK, not per-mount: apps re-mount this on every theme and
    // locale change, and rebuilding the reader each time would re-fetch the member
    // list for a colour swap. Cached on the host object, which is the thing whose
    // lifetime it actually follows.
    const host = clerk;
    host.__vitesseRoster ??= orgRoster(clerk);
    const roster = host.__vitesseRoster;
    // `never` params make the interface accept Clerk's narrower signature; the
    // calls themselves pass real values, so the casts are at the boundary only.
    /*
     * Start the editor's reads NOW, not when its tab is opened.
     *
     * The switcher mounts with the app; the RBAC page is opened later, if at all.
     * Warming here turns the common case from "click, wait a beat, see people" into
     * "click, see people", and costs one request that is very often needed anyway.
     * Keyed by org, so a switch invalidates rather than showing the last org's.
     */
    /*
     * Warm the AUTHORED read only — never the join.
     *
     * The provider's roster is filled ASYNCHRONOUSLY after this runs, so warming the
     * joined value here captures an empty list and freezes it: the editor then shows
     * "no members" for an org that plainly has some. Warming a value that is not
     * ready yet does not make it faster, it makes it wrong.
     *
     * The fabric read is the slow half and the org-scoped one, so it is the half
     * worth starting early. The join happens at mount, against whatever the roster
     * holds by then.
     */
    // Warm all THREE reads, not two: the audit tab is the one an operator opens
    // last and waits on longest, so it is the one that most wants to already be in
    // flight by the time they click it.
    warmRbac(currentOrgId(clerk), { members: opts.members, roles: opts.roles, audit: opts.audit?.events });
    // And billing, for the same reason and more so: its summary is six reads behind
    // one endpoint, and it is the tab an admin opens from a cold dialog — so it was
    // slowest exactly when it was most visible. The cache already existed; nothing
    // was priming it.
    if (opts.billing)
        warmBilling(opts.billing);
    const mount = clerk.mountOrganizationSwitcher;
    const unmount = clerk.unmountOrganizationSwitcher;
    try {
        unmount.call(clerk, el);
    }
    catch {
        /* not mounted yet */
    }
    try {
        mount.call(clerk, el, {
            appearance: opts.appearance,
            hidePersonal: opts.hidePersonal ?? true,
            organizationProfileProps: {
                /*
                 * The appearance has to be passed AGAIN here, not only on the switcher.
                 *
                 * The trigger and the profile modal are separately-themed surfaces to the
                 * provider: theming the switcher leaves the modal on its default light
                 * theme, so an app in dark mode opens a white dialog with black text
                 * sitting on top of it — and the custom pages inside, which DO follow the
                 * app's tokens, then render dark controls on that white ground. The
                 * mixture is worse than either alone.
                 */
                appearance: opts.appearance,
                customPages: [
                    // Billing first: it is the tab an admin opens the org profile to reach.
                    ...(opts.billing
                        ? [
                            billingPage({
                                ...opts.billing,
                                /*
                                 * SEATS COME FROM THE ROSTER THIS MOUNT ALREADY HAS.
                                 *
                                 * Not from each app reaching into the identity provider's member API: the apps
                                 * hold a neutral identity host on purpose, and three copies of "count the members"
                                 * would be three places to update when that seam moves. The roster is already
                                 * built here, cached per host, and shared with the RBAC page.
                                 *
                                 * Only the count this mount can actually answer is supplied. The others stay
                                 * absent, and those rows show their allowance alone rather than a zero that would
                                 * tell an admin their nine-person org uses no seats.
                                 */
                                usedCounts: async () => ({ 'telemetry.max_users': roster.members().length || undefined }),
                            }),
                        ]
                        : []),
                    ...(opts.extraPages ?? []),
                    /*
                     * OUR members screen, before the RBAC one: the dialog reads people →
                     * what they may do, and both tabs share the SAME joined roster so they
                     * cannot disagree about who is in the org.
                     */
                    membersPage({
                        members: () => joinRoster(roster.members, opts.members)(warmed(currentOrgId(clerk), 'members')),
                        organization: () => clerk.organization ?? null,
                        // The roster's write takes the USER ID and the normalised role — the same
                        // two values `OrgMember` already carries, so the option values below are
                        // the normalised spelling rather than the provider's `org:` prefixed one.
                        // A select whose values did not match `m.role` would render every row on
                        // its placeholder.
                        setOrgRole: (m, role) => roster.setOrgRole(m.userId, role === 'admin' ? 'admin' : 'member'),
                        // Ending a membership is the one irreversible thing on this page, so it
                        // asks first — and asks with the person's NAME in the sentence, because
                        // "are you sure?" is answered by knowing who, not by knowing that.
                        removeMember: async (m) => {
                            const ok = globalThis.confirm?.(t('members.confirmRemove', { name: m.name || m.email }));
                            if (!ok)
                                return;
                            await roster.removeMember(m.userId);
                        },
                        roleOptions: () => [
                            { value: 'admin', label: t('perm.orgAdmin') },
                            { value: 'member', label: t('perm.orgMember') },
                        ],
                        canManage: opts.canEdit,
                    }),
                    rbacPage({
                        orgId: () => currentOrgId(clerk),
                        members: joinRoster(roster.members, opts.members),
                        // Still the identity provider's write: it owns org membership, and the
                        // membership OBJECT the roster read is the only handle that can change
                        // a role. Kept even when the roster itself comes from our fabric.
                        setOrgRole: roster.setOrgRole,
                        save: opts.save,
                        roles: opts.roles,
                        saveRoles: opts.saveRoles,
                        specialists: opts.specialists,
                        canEdit: opts.canEdit,
                        canEditRoles: opts.canEditRoles,
                        // Upgrade from inside the dialog navigates the SAME dialog to its Billing page.
                        onUpgrade: () => openOrgBilling(clerk),
                    }),
                    ...(opts.audit
                        ? [
                            auditPage({
                                ...opts.audit,
                                // Reuse the warmed read when this org already has one in flight or
                                // resolved — switching tabs must not re-ask.
                                events: () => warmed(currentOrgId(clerk), 'audit') ?? opts.audit.events(),
                                /*
                                 * Resolve an actor id to a NAME from the roster this mount already
                                 * holds. Without it every entry whose label was not captured at
                                 * write time renders as a raw `user_…` id — technically the record,
                                 * and unreadable as history. The app supplies nothing for this: the
                                 * roster is here, and asking each app to pass a lookup it would
                                 * build from the same list is a third copy of one fact.
                                 */
                                nameFor: (userId) => roster.members().find((m) => m.userId === userId)?.name,
                            }),
                        ]
                        : []),
                ],
            },
        });
    }
    catch {
        /* Clerk not loaded (dev HTTP preview) — cosmetic, skip it */
    }
}
//# sourceMappingURL=org-rbac-page.js.map